import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getConversationMessages } from 'api/support-chat-api';

export const fetchConversationMessages = createAsyncThunk(
  'messages/fetchMessages',
  async ({ conversationId, params }) => {
    const updatedParams = {
      page: 1,
      per_page: '10',
      'q[s]': 'created_at desc',
      ...params,
    };

    const res = await getConversationMessages({ id: conversationId, params: updatedParams });

    return {
      conversationId,
      ...res,
    };
  },
);

export const loadMoreMessages = createAsyncThunk(
  'messages/loadMoreMessages',
  async ({ conversationId }, { getState, rejectWithValue }) => {
    try {
      const state = getState().supportConversationsData?.conversations?.[conversationId];
      const lastConversationid = state?.messages?.[state?.messages?.length - 1]?.id ?? '';

      const params = {
        'q[id_lt]': lastConversationid,
        per_page: '10',
        'q[s]': 'created_at desc',
      };

      const res = await getConversationMessages({ id: conversationId, params });
      return {
        conversationId,
        ...res,
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  },
);

const messagesSlice = createSlice({
  name: 'messages',
  initialState: {
    conversations: {},
    activeConversationId: null,
  },
  reducers: {
    setActiveConversation(state, action) {
      state.activeConversationId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConversationMessages.pending, (state, action) => {
        const { conversationId } = action.meta.arg;
        state.conversations[conversationId] ??= {
          messages: [],
          loading: true,
          error: null,
          hasMore: true,
          loadingMore: false,
          pagination: {},
        };

        state.conversations[conversationId].loading = true;
      })
      .addCase(fetchConversationMessages.fulfilled, (state, action) => {
        const { conversationId, ...res } = action.payload;

        // // If using cache, don't change anything except loading = false. Cache removed
        // if (cached) {
        //   state.conversations[conversationId].loading = false;
        //   return;
        // }

        state.conversations[conversationId].messages = res?.data ?? [];
        state.conversations[conversationId].hasMore = (res?.meta?.pagination?.pages || 1) > 1;
        state.conversations[conversationId].pagination = res?.meta?.pagination ?? {};
        state.conversations[conversationId].lastFetchedAt = Date.now();
        state.conversations[conversationId].loading = false;
        state.conversations[conversationId].loadingMore = false;
      })
      .addCase(fetchConversationMessages.rejected, (state, action) => {
        const { conversationId } = action.meta.arg;
        state.conversations[conversationId].loading = false;
        state.conversations[conversationId].error = action.error.message;
      })
      .addCase(loadMoreMessages.pending, (state, action) => {
        const { conversationId } = action.meta.arg;
        if (state.conversations[conversationId]) {
          state.conversations[conversationId].loadingMore = true;
        }
      })
      .addCase(loadMoreMessages.fulfilled, (state, action) => {
        const { conversationId, ...res } = action.payload;
        const conversation = state.conversations[conversationId];

        if (conversation) {
          conversation.messages = [...conversation.messages, ...(res?.data || [])];
          conversation.hasMore = (res?.meta?.pagination?.pages || 1) > 1;
          conversation.pagination = res?.meta?.pagination || {};
          conversation.loadingMore = false;
        }
      })
      .addCase(loadMoreMessages.rejected, (state, action) => {
        const { conversationId } = action.meta.arg;
        if (state.conversations[conversationId]) {
          state.conversations[conversationId].loadingMore = false;
          state.conversations[conversationId].error = action.error.message;
        }
      });
  },
});

export const { setActiveConversation } = messagesSlice.actions;
export default messagesSlice.reducer;
